import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon, InboxIcon } from "@heroicons/react/24/solid";

import { TbUser, TbMail } from "react-icons/tb";
import { AiOutlinePoweroff } from "react-icons/ai";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../redux/features/userSlice";
import {
  bookingMenu,
  orderingMenu,
  loyaltyOnlyMenu,
  loyaltyMenu,
} from "./MenuData/navigationConfig";
import { MenuSelectTop } from "../components/Form";
import useIsDesktop from "../hook/UseIsDecktop";
import { motion, AnimatePresence } from "framer-motion";
import { XCircleIcon } from "lucide-react";

const Sidebar = ({ isSidebarOpen, forceDetail, toggleSidebar }) => {
  const { user } = useSelector((state) => state.user);

  // console.log({ user });
  const [activeId, setActiveId] = useState(null);
  const isDesktop = useIsDesktop();

  const sidebarRef = useRef(); // ⬅️ ADD THIS

  let navigationConfig = [];

  switch (user?.operatorType) {
    case "booking":
      navigationConfig = [...bookingMenu];
      break;
    case "ordering":
      navigationConfig = [...orderingMenu];
      break;
    case "loyalty-only":
      navigationConfig = [...loyaltyOnlyMenu];
      break;
    default:
      navigationConfig = [...orderingMenu]; // fallback
  }

  const activeConfig = navigationConfig.find((item) => item.id === activeId);

  // ⬇️ Detect click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setActiveId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={sidebarRef} className="flex h-screen text-text">
      <SidebarMiniNav
        activeId={activeId}
        setActiveId={setActiveId}
        navigationConfig={navigationConfig} // 👈 pass it down
      />
      {/* Desktop → toggle logic */}

      {isDesktop && isSidebarOpen && (
        <AnimatePresence mode="wait">
          {activeConfig && (
            <motion.div
              key={activeConfig.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="relative z-50 h-screen bg-zinc-900" // ✅ higher than z-40 header
            >
              <SidebarDetailPanel activeConfig={activeConfig} />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Mobile → open detail if forceDetail is true */}
      {!isDesktop && forceDetail && (
        <motion.div
          key={activeConfig?.id || "default-mobile"}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 40 }}
          transition={{ duration: 0.3 }}
          className="fixed top-0 left-[100px] w-[calc(100vw-100px)] h-full z-60 bg-zinc-900"
        >
          <SidebarDetailPanel
            activeConfig={activeConfig || navigationConfig[0]}
            navigationConfig={navigationConfig} // 👈 pass it
          />
          <button
            onClick={toggleSidebar}
            className="absolute top-5 right-5 text-redlight transition"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </motion.div>
      )}
    </div>
  );
};

const SidebarMiniNav = ({ activeId, setActiveId, navigationConfig }) => {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const DropDown1 = [
    {
      title: "Profile",
      icon: TbUser,
      onClick: () => navigate("/settings"),
    },
    {
      title: "Inbox",
      icon: TbMail,
      onClick: () => navigate("/userInbox"),
    },
    {
      title: "Logout",
      icon: AiOutlinePoweroff,
      onClick: () => {
        dispatch(setUser(null));
        navigate("/");
      },
    },
  ];

  return (
    <div className="w-[100px] flex flex-col items-center justify-between py-6 space-y-6 bg-zinc-900 border-r border-zinc-800 text-text">
      <img src="/images/lokalai_icon.png" className="w-10 h-10" />

      {/* old design */}
      {/* <div className="flex-colo space-y-6">
        <div className="flex-colo space-y-0">
          {navigationConfig.map((section, index) => {
            if (section.type === "divider") {
              return (
                <div
                  key={section.id || `divider-${index}`}
                  className="w-full h-px bg-zinc-600 my-0"
                />
              );
            }

            if (section.type === "group") {
              const isActive = activeId === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() =>
                    setActiveId((prev) =>
                      prev === section.id ? null : section.id
                    )
                  }
                  className={`w-full h-auto py-4 flex flex-col items-center justify-center  ${
                    isActive
                      ? "bg-zinc-800 text-redlight rounded"
                      : "hover:text-redlight hover:bg-dry"
                  }`}
                >
                  <Icon icon={section.icon} width="24" height="24" />
                  <span className="text-[12px] mt-1 leading-tight text-center">
                    {section.title}
                  </span>
                </button>
              );
            }

            return null; // fallback
          })}
        </div>
      </div> */}

      {/* Navigation */}
      <div className="flex flex-col items-center space-y-6">
        {navigationConfig.map((section, index) => {
          if (section.type === "divider") {
            return (
              <div
                key={section.id || `divider-${index}`}
                className="w-16 h-px bg-zinc-600"
              />
            );
          }

          if (section.type === "group") {
            const isActive = activeId === section.id;
            return (
              <div key={section.id} className="relative group">
                <button
                  onClick={() =>
                    setActiveId((prev) =>
                      prev === section.id ? null : section.id
                    )
                  }
                  className={`p-1 hover:p-4 flex flex-col items-center justify-center rounded-lg transition ${
                    isActive
                      ? "bg-zinc-800 text-redlight"
                      : "text-gray-400 hover:text-redlight hover:bg-zinc-700"
                  }`}
                >
                  <Icon
                    icon={section.icon}
                    width={isActive ? 24 : 35}
                    height={isActive ? 24 : 35}
                    className="transition-all duration-200"
                  />{" "}
                  {isActive && (
                    <span className="text-[12px] mt-1 leading-tight text-center">
                      {section.title}
                    </span>
                  )}
                </button>
                {/* Tooltip */}
                <div className="absolute left-20 top-1/2 -translate-y-1/2 px-4 py-2 text-xs text-white bg-zinc-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-[999]">
                  {section.title}
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>

      <MenuSelectTop datas={DropDown1}>
        <div className="w-full flex items-center justify-center p-0 lg:p-4 relative group">
          <img
            src={user?.photo}
            alt="user"
            className="w-12 h-12 border border-yellowlight object-cover rounded-full"
          />
          {/* Tooltip */}
          <div
            className="absolute bottom-20 left-[100px] -translate-x-1/2 whitespace-nowrap
                    bg-zinc-800 text-white text-xs px-3 py-1 rounded-md opacity-0 
                    group-hover:opacity-100 transition duration-200 pointer-events-none shadow-lg"
          >
            <p className="font-semibold">{user?.name}</p>
            <p className="text-gray-400">{user?.town}</p>
          </div>
        </div>
      </MenuSelectTop>
    </div>
  );
};

const SidebarDetailPanel = ({ activeConfig }) => (
  <div className="w-[280px] max-h-[95vh] bg-zinc-900 px-6 py-5 overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-600 text-text ">
    <div className="mb-6">
      <h3 className="text-md font-bold text-redlight uppercase">
        {activeConfig.title}
      </h3>
      <p className="text-[11px] text-textGray">{activeConfig.subtitle}</p>
    </div>

    <div className="space-y-6">
      {activeConfig.children.map((item) =>
        item.type === "item" ? (
          <NavLink
            key={item.id}
            to={item.url}
            className="flex items-center text-sm space-x-2 hover:text-redlight"
          >
            <Icon icon={item.icon} width="18" />
            <span>{item.title}</span>
            {item.badge && (
              <span className="ml-auto bg-redlight text-text text-xs px-2 py-0.5 rounded-full">
                {item.badge.title}
              </span>
            )}
          </NavLink>
        ) : item.type === "collapse" ? (
          <Disclosure key={item.id} defaultOpen>
            {({ open }) => (
              <>
                <Disclosure.Button className="w-full flex items-center justify-between text-sm hover:text-redlight">
                  <div className="flex space-x-2 items-center">
                    <Icon icon={item.icon} width="18" />
                    <span>{item.title}</span>
                  </div>
                  <ChevronDownIcon
                    className={`h-4 w-4 transform transition-transform ${
                      open ? "rotate-0" : "-rotate-90"
                    }`}
                  />
                </Disclosure.Button>
                <Disclosure.Panel className="ml-4 mt-1 space-y-4">
                  {item.children.map((child) => (
                    <NavLink
                      key={child.id}
                      to={child.url}
                      className="block text-xs text-gray-400 hover:text-redlight"
                    >
                      {child.title}
                    </NavLink>
                  ))}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        ) : null
      )}
    </div>
  </div>
);

export default Sidebar;
